// Saving a tool set: the frontend half of the C++ `Ui_save_tools_as_set` port.
//
// The open tool windows are separate webviews, so their state is collected by
// a broadcast round-trip: the Tool Sets dialog emits a snapshot request, every
// tool window answers with its dialog descriptor (the same shape
// `load_tool_set` returns, including its current window rect), and the
// collected descriptors go to the `save_tool_set` command, which writes the
// ini section. Events route through the Rust `broadcast_event` command — a
// frontend emit() does not reliably reach other webviews in Tauri v2.

import { useEffect, useRef } from "react";
import type { ToolSetDialog, ToolSetRect } from "./toolSetLoad";
import { isToolWindowLabel, liveScope, countToolWindows } from "./closeToolWindows";

function isTauri(): boolean {
  return typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;
}

function hasBroadcastChannel(): boolean {
  return typeof BroadcastChannel !== "undefined";
}

export const TOOLSET_SNAPSHOT_REQUEST = "toolset-snapshot-request";
export const TOOLSET_SNAPSHOT_RESPONSE = "toolset-snapshot-response";

// Web build: the tool windows are separate browser tabs, so the snapshot
// round-trip rides a same-origin BroadcastChannel instead of the Tauri
// `broadcast_event` command. The main window posts a `request`; each in-scope
// tab answers with a `response` carrying its descriptor.
const TOOLSET_SNAPSHOT_CHANNEL = "tiger-toolset-snapshot";

type SnapshotChannelMessage =
  | { type: "request"; requestId: string; scope: string }
  | { type: "response"; requestId: string; id: string; dialog: ToolSetDialog | null };

/** This tab's outer rect in screen pixels — the web analogue of the Tauri
 *  `currentWindowRect`. A window.open popup exposes its geometry via screenX/Y +
 *  outerWidth/Height; fall back to the inner size if a browser zeroes the outer
 *  values. The report uses width/height to size each rendered graph. */
function currentWebWindowRect(): ToolSetRect | null {
  if (typeof window === "undefined") return null;
  const left = Math.round(window.screenX ?? 0);
  const top = Math.round(window.screenY ?? 0);
  const width = Math.round(window.outerWidth || window.innerWidth || 0);
  const height = Math.round(window.outerHeight || window.innerHeight || 0);
  return { left, top, right: left + width, bottom: top + height };
}

/** C++ `Ui_save_tools_as_set` save order: graphs, then data grids, then
 *  diagnostics. Used to order the collected descriptors on web (the desktop
 *  derives the same order from the window label prefixes). */
function kindOrder(d: ToolSetDialog): number {
  return d.kind === "graph" ? 0 : d.kind === "data_grid" ? 1 : 2;
}

// Omit that distributes over the ToolSetDialog union (a plain Omit collapses
// a union to its common keys).
type DistributedOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;

/** A window's dialog descriptor minus the rect (filled in by the responder). */
export type ToolSetDialogBody = DistributedOmit<ToolSetDialog, "rect">;

interface SnapshotRequest {
  requestId: string;
}

interface SnapshotResponse {
  requestId: string;
  label: string;
  /** null when the window has nothing to save (e.g. a multi-time graph the
   *  ini format can't represent) — the window is then skipped. */
  dialog: ToolSetDialog | null;
}

/** The responding window's outer rect in logical pixels — the same unit the
 *  spawn bounds use, so a saved rect reopens the window where it was. */
async function currentWindowRect(): Promise<ToolSetRect | null> {
  try {
    const { getCurrentWindow } = await import("@tauri-apps/api/window");
    const win = getCurrentWindow();
    const [pos, size, scale] = await Promise.all([
      win.outerPosition(),
      win.outerSize(),
      win.scaleFactor(),
    ]);
    return {
      left: Math.round(pos.x / scale),
      top: Math.round(pos.y / scale),
      right: Math.round((pos.x + size.width) / scale),
      bottom: Math.round((pos.y + size.height) / scale),
    };
  } catch {
    return null;
  }
}

/**
 * Web (browser-tab) snapshot responder. Answers `tiger-toolset-snapshot`
 * requests that match this tab's `?live=` scope with its dialog descriptor (the
 * Tauri path's `broadcast_event` equivalent). `id` tags the response so the
 * collector can order tabs by creation time; defaults to a fresh token. Returns
 * a cleanup fn. No-op on desktop / where BroadcastChannel is unavailable.
 */
export function registerToolSetSnapshotResponder(
  build: () => ToolSetDialogBody | null,
  opts?: { scope?: string; id?: string },
): () => void {
  if (typeof window === "undefined" || isTauri() || !hasBroadcastChannel()) {
    return () => {};
  }
  const scope = opts?.scope ?? liveScope();
  const id = opts?.id ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const ch = new BroadcastChannel(TOOLSET_SNAPSHOT_CHANNEL);
  ch.onmessage = (ev: MessageEvent) => {
    const m = ev.data as SnapshotChannelMessage | null;
    if (!m || m.type !== "request" || m.scope !== scope) return;
    let dialog: ToolSetDialog | null = null;
    try {
      const body = build();
      if (body) dialog = { ...body, rect: currentWebWindowRect() } as ToolSetDialog;
    } catch (err) {
      console.error("tool-set snapshot build failed:", err);
    }
    ch.postMessage({ type: "response", requestId: m.requestId, id, dialog } satisfies SnapshotChannelMessage);
  };
  return () => {
    ch.onmessage = null;
    ch.close();
  };
}

export function useToolSetSnapshotResponder(build: () => ToolSetDialogBody | null) {
  const buildRef = useRef(build);
  useEffect(() => {
    buildRef.current = build;
  });

  useEffect(() => {
    // Web build: register a BroadcastChannel responder instead of a Tauri
    // event listener (tool windows are browser tabs, not webviews).
    if (!isTauri()) {
      return registerToolSetSnapshotResponder(() => buildRef.current());
    }
    let unlisten: (() => void) | undefined;
    let unmounted = false;
    void (async () => {
      const [{ listen }, { getCurrentWindow }, { invoke }] = await Promise.all([
        import("@tauri-apps/api/event"),
        import("@tauri-apps/api/window"),
        import("@tauri-apps/api/core"),
      ]);
      const stop = await listen<SnapshotRequest>(TOOLSET_SNAPSHOT_REQUEST, async (e) => {
        let dialog: ToolSetDialog | null = null;
        try {
          const body = buildRef.current();
          if (body) dialog = { ...body, rect: await currentWindowRect() } as ToolSetDialog;
        } catch (err) {
          console.error("tool-set snapshot build failed:", err);
        }
        const payload: SnapshotResponse = {
          requestId: e.payload.requestId,
          label: getCurrentWindow().label,
          dialog,
        };
        await invoke("broadcast_event", { event: TOOLSET_SNAPSHOT_RESPONSE, payload });
      });
      // The effect may have been cleaned up while listen() was registering.
      if (unmounted) stop();
      else unlisten = stop;
    })();
    return () => {
      unmounted = true;
      unlisten?.();
    };
  }, []);
}

/**
 * Web (browser) collector: post a snapshot request on the BroadcastChannel and
 * gather each in-scope tab's descriptor. The expected responder count comes from
 * the tool-window registry (`countToolWindows`, scoped by `?live=`), so this
 * settles as soon as every open tab has answered; `timeoutMs` bounds the wait if
 * a backgrounded tab is slow. Ordered graphs → grids → diagnostics like the
 * desktop, with a creation-time (id) tiebreak.
 */
async function collectToolSetDialogsWeb(timeoutMs: number): Promise<ToolSetDialog[]> {
  if (typeof window === "undefined" || !hasBroadcastChannel()) return [];
  // countToolWindows starts the registry, settles its reconcile ping, and
  // returns the count of open tool tabs in this window's `?live=` scope.
  const expected = await countToolWindows();
  if (expected === 0) return [];

  const scope = liveScope();
  const requestId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const responses = new Map<string, ToolSetDialog | null>(); // tab id -> descriptor

  let settle!: () => void;
  const allAnswered = new Promise<void>((resolve) => {
    settle = resolve;
  });

  const ch = new BroadcastChannel(TOOLSET_SNAPSHOT_CHANNEL);
  ch.onmessage = (ev: MessageEvent) => {
    const m = ev.data as SnapshotChannelMessage | null;
    if (!m || m.type !== "response" || m.requestId !== requestId) return;
    responses.set(m.id, m.dialog);
    if (responses.size >= expected) settle();
  };

  try {
    ch.postMessage({ type: "request", requestId, scope } satisfies SnapshotChannelMessage);
    await Promise.race([allAnswered, new Promise((r) => setTimeout(r, timeoutMs))]);
  } finally {
    ch.onmessage = null;
    ch.close();
  }

  return [...responses.entries()]
    .filter((e): e is [string, ToolSetDialog] => e[1] != null)
    .sort(([ia, da], [ib, db]) => kindOrder(da) - kindOrder(db) || ia.localeCompare(ib))
    .map(([, d]) => d);
}

/**
 * Collect a dialog descriptor from every open tool window. Resolves when all
 * expected windows have answered, or after `timeoutMs` with whatever arrived
 * (a hung window shouldn't block saving the rest). Descriptors come back in
 * the C++ save order: graphs, then data grids, then diagnostics.
 */
export async function collectToolSetDialogs(timeoutMs = 3000): Promise<ToolSetDialog[]> {
  if (!isTauri()) return collectToolSetDialogsWeb(timeoutMs);
  const [{ getAllWebviewWindows }, { listen }, { invoke }] = await Promise.all([
    import("@tauri-apps/api/webviewWindow"),
    import("@tauri-apps/api/event"),
    import("@tauri-apps/api/core"),
  ]);

  const expected = (await getAllWebviewWindows())
    .map((w) => w.label)
    .filter(isToolWindowLabel);
  if (expected.length === 0) return [];

  const requestId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const responses = new Map<string, ToolSetDialog | null>();

  let settle!: () => void;
  const allAnswered = new Promise<void>((resolve) => {
    settle = resolve;
  });

  const unlisten = await listen<SnapshotResponse>(TOOLSET_SNAPSHOT_RESPONSE, (e) => {
    if (e.payload.requestId !== requestId) return;
    responses.set(e.payload.label, e.payload.dialog);
    if (expected.every((label) => responses.has(label))) settle();
  });

  try {
    await invoke("broadcast_event", {
      event: TOOLSET_SNAPSHOT_REQUEST,
      payload: { requestId } satisfies SnapshotRequest,
    });
    await Promise.race([allAnswered, new Promise((r) => setTimeout(r, timeoutMs))]);
  } finally {
    unlisten();
  }

  // C++ `Ui_save_tools_as_set` writes graphs, then data grids, then
  // diagnostics; label timestamps keep windows of one type in creation order.
  const typeOrder = (label: string) =>
    label.startsWith("graph-") ? 0 : label.startsWith("datagrid-") ? 1 : 2;
  return expected
    .slice()
    .sort((a, b) => typeOrder(a) - typeOrder(b) || a.localeCompare(b))
    .map((label) => responses.get(label))
    .filter((d): d is ToolSetDialog => d != null);
}

export interface ToolSetSaveResult {
  /** false when the name belongs to a default (read-only) set. */
  saved: boolean;
  /** Tool windows captured into the set. */
  count: number;
}

/**
 * Save the currently open tool windows as a user tool set. Port of the Tool
 * Sets dialog's Save action (C++ `OnToolSetSaveButton` → `Ui_save_tools_as_set`).
 */
export async function saveToolSet(name: string): Promise<ToolSetSaveResult> {
  // collectToolSetDialogs dispatches to the BroadcastChannel collector on web and
  // the Tauri snapshot round-trip on the desktop, so the same call works for both.
  const dialogs = await collectToolSetDialogs();
  if (isTauri()) {
    const { invoke } = await import("@tauri-apps/api/core");
    const saved = await invoke<boolean>("save_tool_set", { name, dialogs });
    return { saved, count: dialogs.length };
  }
  // Web (webserver): POST the collected descriptors to the login-scoped writer.
  // `saved` is false when the name belongs to a default (read-only) set.
  const resp = await fetch("/api/save_tool_set", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, dialogs }),
  });
  const saved = resp.ok && ((await resp.json()) as boolean);
  return { saved, count: dialogs.length };
}
